import { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { identifyUser } from '@/lib/errorTracking';
import type { User, AuthResponse, AuthError } from '@supabase/supabase-js';

// Founder emails. One person (Patrick, ruled 2026-09-27). The database's
// v2.profiles.is_admin is the real gate; this list only covers the email
// identities that have no profile row.
export const ADMIN_EMAILS = [
  'patrick@maslownyc.com',
  'patrick@maslow.nyc',
];

interface SendPhoneOtpParams {
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  shouldCreateUser?: boolean;
}

interface VerifyPhoneOtpParams {
  phone: string;
  token: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isFounder: boolean;
  founderChecked: boolean;
  sendPhoneOtp: (params: SendPhoneOtpParams) => Promise<{ error: AuthError | null }>;
  verifyPhoneOtp: (params: VerifyPhoneOtpParams) => Promise<AuthResponse>;
  signOut: () => Promise<{ error: AuthError | null }>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Read email from auth.users.email, falling back to raw_user_meta_data.email.
// Phone-OTP users do not have auth.users.email populated; their email lives in metadata.
const getEffectiveEmail = (user: User | null | undefined): string | null => {
  if (!user) return null;
  if (user.email) return user.email;
  const metadataEmail = (user.user_metadata as Record<string, unknown> | undefined)?.email;
  return typeof metadataEmail === 'string' ? metadataEmail : null;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFounder, setIsFounder] = useState(false);
  // Has the founder check actually resolved yet? ProtectedRoute waits on this for
  // requireFounder routes so it doesn't redirect on the initial false isFounder
  // before the deferred checkFounderStatus has returned.
  const [founderChecked, setFounderChecked] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Settle `loading` the moment we know the user, independent of the founder
    // check. The founder query is run separately (fire-and-forget) so that a slow,
    // failed, or deadlocked profile lookup can never leave `loading` stuck true and
    // ProtectedRoute spinning forever.
    const settleSession = (sessionUser: User | null) => {
      if (!mounted) return;
      setUser(sessionUser);
      setLoading(false);
      // Tag error reports with the user id ONLY — never the phone or email.
      // Enough to tell "this broke for one person" from "this broke for everyone".
      identifyUser(sessionUser?.id ?? null);
    };

    // Check active session on mount.
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        settleSession(session?.user ?? null);
        if (session?.user) {
          checkFounderStatus(session.user.id, getEffectiveEmail(session.user));
        } else {
          // No user — nothing to check.
          setIsFounder(false);
          setFounderChecked(true);
        }
      })
      .catch((err) => {
        console.error("Session check failed", err);
        settleSession(null);
        setIsFounder(false);
        setFounderChecked(true);
      });

    // Listen for changes on auth state.
    // The callback is intentionally NOT async: awaiting a Supabase data query from
    // inside it deadlocks against the auth client's internal lock (the query waits
    // for the lock the callback is holding), which is what hung the founder check
    // and left loading pending. We defer the DB call so it runs after the callback
    // returns and the lock is released.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      settleSession(session?.user ?? null);
      if (session?.user) {
        const { id } = session.user;
        const effectiveEmail = getEffectiveEmail(session.user);
        setTimeout(() => {
          if (mounted) checkFounderStatus(id, effectiveEmail);
        }, 0);
      } else {
        setIsFounder(false);
        setFounderChecked(true);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkFounderStatus = async (userId: string, effectiveEmail: string | null) => {
    const isAdminByEmail = !!(effectiveEmail && ADMIN_EMAILS.includes(effectiveEmail.toLowerCase()));

    try {
      const { data, error } = await ((supabase as any)
        .schema('v2').from('profiles') as any)
        .select('is_admin')
        .eq('id', userId)
        .single();

      if (!error && data) {
        setIsFounder(data.is_admin === true || isAdminByEmail);
        return;
      }

      if (error) {
        console.error("Founder check query failed:", error);
      }
    } catch (error) {
      console.error("Founder check failed:", error);
    } finally {
      // Mark the check resolved on both success and error paths so requireFounder
      // routes stop waiting.
      setFounderChecked(true);
    }

    setIsFounder(isAdminByEmail);
  };

  const sendPhoneOtp = async ({
    phone,
    firstName,
    lastName,
    email,
    shouldCreateUser,
  }: SendPhoneOtpParams): Promise<{ error: AuthError | null }> => {
    try {
      const metadata: Record<string, unknown> = {};
      if (firstName !== undefined) metadata.first_name = firstName;
      if (lastName !== undefined) metadata.last_name = lastName;
      if (email !== undefined) metadata.email = email;

      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: {
          shouldCreateUser: shouldCreateUser ?? true,
          ...(Object.keys(metadata).length > 0 ? { data: metadata } : {}),
        },
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const verifyPhoneOtp = async ({ phone, token }: VerifyPhoneOtpParams): Promise<AuthResponse> => {
    try {
      return await supabase.auth.verifyOtp({ phone, token, type: 'sms' });
    } catch (error) {
      return { data: { user: null, session: null }, error: error as AuthError };
    }
  };

  const signOut = async (): Promise<{ error: AuthError | null }> => {
    try {
      return await supabase.auth.signOut();
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  // Exposed value
  const value: AuthContextType = {
    sendPhoneOtp,
    verifyPhoneOtp,
    signOut,
    user,
    loading,
    isFounder,
    founderChecked,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
